/**
 * Single GraphQL entry point for AppSync (Amplify v6).
 * All queries, mutations, and subscriptions go through here — no ad-hoc generateClient() elsewhere.
 */
import { generateClient } from "aws-amplify/api";

/** Lazy so `Amplify.configure` in index.jsx runs before first GraphQL call (imports hoist above configure). */
let client;
function getClient() {
  if (!client) {
    client = generateClient();
  }
  return client;
}

/** Maps Gen1 / v5 auth mode strings to v6 GraphQLAuthMode values. */
const AUTH_MODE_MAP = {
  AMAZON_COGNITO_USER_POOLS: "userPool",
  AWS_IAM: "iam",
  AMAZON_OPENID_CONNECT: "oidc",
  API_KEY: "apiKey",
  AWS_LAMBDA: "lambda",
  NONE: "none",
};

/**
 * @param {string | undefined} authMode
 * @returns {string | undefined}
 */
export function normalizeGraphQLAuthMode(authMode) {
  if (authMode == null) return undefined;
  return AUTH_MODE_MAP[authMode] ?? authMode;
}

/**
 * GraphQL query or mutation.
 * @param {import('aws-amplify/api').GraphQLOptions} options
 * @returns {Promise<import('@aws-amplify/api-graphql').GraphQLResult>}
 */
export function graphqlRequest(options) {
  const { authMode, ...rest } = options;
  const normalized = normalizeGraphQLAuthMode(authMode);
  return getClient().graphql({
    ...rest,
    ...(normalized !== undefined ? { authMode: normalized } : {}),
  });
}

/**
 * GraphQL subscription — same underlying client; use for readability at call sites.
 * Subscription payloads use `{ data }` (v6), not v5 `{ value: { data } }`.
 * @param {import('aws-amplify/api').GraphQLOptions} options
 * @returns {import('rxjs').Observable<import('@aws-amplify/api-graphql').GraphqlSubscriptionMessage<unknown>>}
 */
export function graphqlSubscribe(options) {
  return graphqlRequest(options);
}
